import { Block, Flex, Typography, colors } from '@aloft/toolkit-sui';

import React, { useMemo } from 'react';

import { PropertyGrid } from '../../components/PropertyGrid/PropertyGrid.tsx';
import { PropertyGridFocusProvider } from '../../components/PropertyGrid/PropertyGridFocusProvider.tsx';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { selectHasArvOption } from '../../redux/features/selectors/orderSelectors.ts';
import {
  selectAivReconciliationData,
  selectArvReconciliationData,
  selectReconciliationData,
} from '../../redux/features/selectors/selectReconciliationData.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { AdjustmentsToolbar } from '../adjustments/components/AdjustmentsToolbar.tsx';
import { AdjustmentsModuleTitle } from '../adjustments/internal/AdjustmentsModuleTitle.tsx';
import { AdjustmentsGridAdditionalFeaturesSection } from './columnSections/AdjustmentsGridAdditionalFeaturesSection.tsx';
import { AdjustmentsGridAdjustedSPSection } from './columnSections/AdjustmentsGridAdjustedSPSection.tsx';
import { AdjustmentsGridDateOfSaleSection } from './columnSections/AdjustmentsGridDateOfSaleSection.tsx';
import { AdjustmentsGridSiteExtractionSection } from './columnSections/AdjustmentsGridSiteExtractionSection.tsx';
import { AdjustmentsGridMainDwellingSection } from './columnSections/AdjustmentsMainDwellingCostSection.tsx';

const AvgAdjSalePriceLabel = () => {
  const hasArvOption = useAppSelector(selectHasArvOption);
  const allData = useAppSelector(selectReconciliationData);
  const aivData = useAppSelector(selectAivReconciliationData);
  const arvData = useAppSelector(selectArvReconciliationData);

  const avgAdjSalePrice = allData?.averageCompMarketAdjustedSalePrice?.toString(true) ?? 'N/A';
  const avgAivSalePrice = aivData?.averageCompMarketAdjustedSalePrice?.toString(true) ?? 'N/A';
  const avgArvSalePrice = arvData?.averageCompMarketAdjustedSalePrice?.toString(true) ?? 'N/A';

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
      gap="8px"
      padding="1rem"
      marginBottom="12px"
      borderRadius="8px"
      borderColor="statusWarning"
      backgroundColor="alertWarningLight"
    >
      <Typography variant="body1">Avg. Adj. Sale Price</Typography>
      {hasArvOption ? (
        <>
          <Flex gap="8px" alignItems="center">
            <Typography
              color={colors.aivText as any}
              __dangerDoNotUseSx={{
                backgroundColor: colors.aiv,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              AIV
            </Typography>
            <Typography variant="h4">{avgAivSalePrice}</Typography>
          </Flex>
          <Flex gap="8px" alignItems="center">
            <Typography
              color="textOffset"
              __dangerDoNotUseSx={{
                backgroundColor: colors.arv,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              ARV
            </Typography>
            <Typography variant="h4">{avgArvSalePrice}</Typography>
          </Flex>
        </>
      ) : (
        <Typography variant="h4">{avgAdjSalePrice}</Typography>
      )}
    </Flex>
  );
};

export const AdjustmentsGridModule = React.memo(() => {
  const referenceBarEvents = useTkv2ReferenceBarEvents();

  const columnSections = useMemo(
    () => [
      AdjustmentsGridDateOfSaleSection,
      AdjustmentsGridSiteExtractionSection,
      AdjustmentsGridMainDwellingSection,
      AdjustmentsGridAdditionalFeaturesSection,
      AdjustmentsGridAdjustedSPSection,
    ],
    [],
  );

  return (
    <PropertyGridFocusProvider>
      <Flex {...referenceBarEvents} flexDirection="column" overflow="hidden" width="100%" gap="8px">
        <Block margin="10px 20px 0 10px">
          <AdjustmentsModuleTitle />
          <AdjustmentsToolbar />
        </Block>
        <Block overflow="auto" width="100%" paddingRight="10px">
          <PropertyGrid
            gridType="AdjustmentsGrid"
            labelColumnHeader={<AvgAdjSalePriceLabel />}
            columnSections={columnSections}
          />
        </Block>
      </Flex>
    </PropertyGridFocusProvider>
  );
});
